import { getOwnValue } from "./get-own-value";

const MIME_DISPLAY_NAMES: Record<string, string> = {
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "image/jpg": "JPEG",
  "image/gif": "GIF",
  "image/webp": "WebP",
  "image/svg+xml": "SVG",
  "image/heic": "HEIC",
  "image/heif": "HEIF",
  "image/bmp": "BMP",
  "image/tiff": "TIFF",
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PPTX",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.ms-word": "DOC",
  "application/vnd.ms-powerpoint": "PPT",
  "application/msword": "DOC",
  "application/json": "JSON",
  "application/xml": "XML",
  "application/zip": "ZIP",
  "text/plain": "TXT",
  "text/csv": "CSV",
  "text/markdown": "Markdown",
  "text/html": "HTML",
};

// NOTE: NSAI-5414 — the MIME type is whatever the uploader's client declared,
// stored verbatim. A plain `MIME_DISPLAY_NAMES[mimeType]` answered
// `Object.prototype` for "__proto__" (an object React cannot render as a
// child) and a function for "toString", so the label is looked up by own key
// only.
const getCuratedDisplayName = (mimeType: string): string | undefined =>
  getOwnValue({ record: MIME_DISPLAY_NAMES, key: mimeType });

// NOTE: A derived label earns its place only while it stays about as short as
// a file extension. "MP4" and "MPEG" do; "OCTET-STREAM" and "PACKAGE-ARCHIVE"
// do not — and those are precisely the types whose glyph is already the
// generic one, so the length buys no information.
const MAX_DERIVED_LABEL_LENGTH = 5;

// NOTE: NSAI-4870 — whether the label is short enough to sit beside a file
// name rather than compete with it. Curated names always qualify (they are
// chosen to be short, and "Markdown" is deliberate); a derived one qualifies
// on length alone. Callers that lay the label out inline — the mobile chat
// chip puts it ahead of the name, and the name is what identifies the file —
// ask this first. Callers with a column of their own (web's generated-files
// table) can keep showing every label.
export const hasCompactFileTypeLabel = (mimeType: string): boolean =>
  getCuratedDisplayName(mimeType) !== undefined ||
  formatFileType(mimeType).length <= MAX_DERIVED_LABEL_LENGTH;

export const formatFileType = (mimeType: string): string => {
  const curatedName = getCuratedDisplayName(mimeType);
  if (curatedName !== undefined) {
    return curatedName;
  }
  // NOTE: Fallback — extract last meaningful segment and uppercase it
  // (e.g. "application/vnd.foo.bar+xml" → "XML", "audio/mpeg" → "MPEG").
  const subtype = mimeType.split("/").pop() ?? mimeType;
  const lastSegment = subtype.split(/[.+]/).pop() ?? subtype;
  return lastSegment.toUpperCase();
};

// NOTE: NSAI-4870 — the label a caller should actually render inline, or null
// when it has not earned the space. Both mobile chat surfaces ask the same
// question ("is it compact? then format it"), and answering it in one place
// keeps them from drifting apart if the compact rule changes again.
export const getCompactFileTypeLabel = (mimeType: string): string | null =>
  hasCompactFileTypeLabel(mimeType) ? formatFileType(mimeType) : null;

export type TFriendlyFileTypeKey =
  | "image"
  | "video"
  | "audio"
  | "csv"
  | "html"
  | "text"
  | "pdf"
  | "json"
  | "zip"
  | "word"
  | "spreadsheet"
  | "presentation"
  | "google_doc"
  | "google_sheet"
  | "google_slides"
  | "file";

// NOTE: NSAI-4424 / NSAI-6519 — returns an i18n key under the `file_type.*` block of the common
// namespace; callers translate it via t(`file_type.${key}`, { ns: "common" }).
export const getFriendlyFileType = (mimeType: string): TFriendlyFileTypeKey => {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("text/")) {
    if (mimeType === "text/csv") return "csv";
    if (mimeType === "text/html") return "html";
    return "text";
  }
  switch (mimeType) {
    case "application/pdf":
      return "pdf";
    case "application/json":
      return "json";
    case "application/zip":
      return "zip";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "word";
    case "application/msword":
      return "word";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "spreadsheet";
    case "application/vnd.ms-excel":
      return "spreadsheet";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return "presentation";
    case "application/vnd.ms-powerpoint":
      return "presentation";
    case "application/vnd.google-apps.document":
      return "google_doc";
    case "application/vnd.google-apps.spreadsheet":
      return "google_sheet";
    case "application/vnd.google-apps.presentation":
      return "google_slides";
    default:
      return "file";
  }
};
